import { failure, isFailure, isPending, pending, Result, SigningMethod } from "../common/common";
import { logError } from "../utils/log";
import { ChangeEvent } from "react";
import { when } from "../utils/functions";
import {
  DCEvent,
  EventDispatcher,
  httpGet,
  httpPost,
  HttpResponse,
  reload,
} from "../events/eventsDomain";
import { TMessage } from "../i18n/tt";
import { IconName } from "../components-v2/iconMapping";
import { IconColor } from "../components-v2/Icon";
import { identity } from "lodash";
import { applicationFrontPageUrl, contactUsUrl } from "../utils/navigation";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { toQueryString } from "../utils/queryParams";
import { loginRedirectKey } from "../Login/loginDomain";
import { downloadEvent } from "../api/DownloadService";
import { AppBarData } from "../appBar/appBarDomain";
import { Language } from "../i18n/languages";
import { LandingPageHeaderProps } from "../common/landingPageComponents";
import { prepLandingPageHeader } from "../common/landingPageDomain";

///
/// Signing page
///

export type PageMode = "single" | "multi";

export type DocumentsToSignResponse = {
  documents: DocumentToSign[];
};

export type DocumentToSign = {
  signerName: string;
  signerInitials: string;
  signerEmail?: string;
  authorName: string;
  companyName: string;
  documentTitle: string;
  documentReference: string;
  documentUrl: string;
  signingMethod: SigningMethod;
  isSigned: boolean;
};

export type SigningFont = "Sacramento" | "Grechen Feumen" | "Dancing Script" | "Parisienne";

export const signingFonts: SigningFont[] = [
  "Sacramento",
  "Grechen Feumen",
  "Dancing Script",
  "Parisienne",
];

export type InvalidRefState = { type: "invalid-ref"; signingDetails?: DocumentToSign };

export type LoadingDetailsState = {
  type: "loading-details";
  ref: string;
  batchId?: string;
  signingDetails?: DocumentToSign;
  returnUrl?: string;
};

export type SignedState = {
  type: "signed";
  ref: string;
  signingDetails: DocumentToSign;
  isDownloadingDocument?: boolean;
};

export type ReadyState = {
  type: "ready";
  ref: string;
  batchId?: string;
  signingDetails: DocumentToSign;
  isStartingSigning?: boolean;
  returnUrl?: string;
  mode?: PageMode;
  isDownloadingDocument?: boolean;
};

export type SignWithEmailState = {
  type: "sign-with-email";
  ref: string;
  batchId?: string;
  signingDetails: DocumentToSign;
  verificationCode: string;
  signResult?: Result;
  isDownloadingDocument?: boolean;
};

export type SignWithHandwritingState = {
  type: "sign-with-handwriting";
  ref: string;
  batchId?: string;
  signingDetails: DocumentToSign;
  inputMode: "draw" | "type";
  typedText: string;
  font: SigningFont;
  signResult?: Result;
  reactSketchCanvasRef?: ReactSketchCanvas;
  isDownloadingDocument?: boolean;
};

export type SignDocumentPageState =
  | InvalidRefState
  | LoadingDetailsState
  | ReadyState
  | SignedState
  | SignWithEmailState
  | SignWithHandwritingState;

export type HeaderUIState = {
  title: TMessage;
  links: { id: string; label: TMessage; onClick: () => void }[];
  header: LandingPageHeaderProps;
};

export type DoneStateUIState = {
  type: "done";
  header: HeaderUIState;
  iconName: IconName;
  iconColor: IconColor;
  title: TMessage;
  description?: TMessage;
  primaryAction?: { label: TMessage; onClick: () => void };
  document?: { type: "pdf"; url: string } | { type: "html"; content: string };
  alertType: "info" | "warning" | "error" | "success";
  downloadDocument?: () => void;
  isDownloadingDocument?: boolean;
};

export type LoadingUIState = { type: "loading"; header: HeaderUIState };

export type ReadyUIState = {
  type: "ready";
  header: HeaderUIState;
  title: TMessage;
  description: TMessage;
  document?: { type: "pdf"; url: string } | { type: "html"; content: string };
  startSigning: {
    label: TMessage;
    onClick: () => void;
    loading?: boolean;
  };
  startBatchSigning?: {
    label: TMessage;
    onClick: () => void;
    loading?: boolean;
  };
  downloadDocument?: () => void;
  isDownloadingDocument?: boolean;
  authorName: string;
  companyName: string;
};

export type SignWithEmailUIState = {
  type: "sign-with-email";
  header: HeaderUIState;
  message: TMessage;
  verificationCode: {
    label: TMessage;
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  submitVerificationCode: {
    label: TMessage;
    onClick: () => void;
    loading?: boolean;
  };
};

export type SignWithHandwritingUIState = {
  type: "sign-with-handwriting";
  header: HeaderUIState;
  message: TMessage;
  inputMode: {
    selected: "draw" | "type";
    options: ["draw", "type"];
    idFn: (value: "draw" | "type") => string;
    labelFn: (value: "draw" | "type") => TMessage;
    onChange: (event: ChangeEvent<HTMLInputElement>, value: "draw" | "type") => void;
  };
  font: {
    value: SigningFont;
    items: SigningFont[];
    onSelect: (value: SigningFont) => void;
    idFn: (value: SigningFont) => string;
    labelFn: (value: SigningFont) => TMessage;
  };
  typedText: {
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  submit?: {
    label: TMessage;
    loading?: boolean;
    onClick: () => void;
  };
  setReactSketchCanvasRef: (ref: ReactSketchCanvas | null) => void;
};

export type SignDocumentPageUIState =
  | DoneStateUIState
  | LoadingUIState
  | ReadyUIState
  | SignWithEmailUIState
  | SignWithHandwritingUIState;

/**
 * This function is used to narrow the PreparedState discriminated union
 * which uses a nested key as the discriminator, something which typescript
 * doesn't recognise (yet).
 *
 * @see https://github.com/microsoft/TypeScript/issues/18758
 */
export const isPreparedPageState = <
  M extends { state: { type: string } },
  T extends M["state"]["type"],
  R extends Extract<M, { state: { type: T } }>,
>(
  model: M,
  type: T,
): model is R => model.state.type === type;

export const signingApi = {
  getSigningDetails: ({
    ref,
    onSuccess,
    onFailure,
  }: {
    ref: string;
    onSuccess: (res: HttpResponse<DocumentsToSignResponse>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => httpGet({ url: `/sign/signing-details/${ref}`, onSuccess, onFailure }),

  signWithEmail: ({
    ref,
    batchId,
    onSuccess,
    onFailure,
  }: {
    ref: string;
    batchId?: string;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpGet({
      url: batchId ? `/sign/sign-batch-with-email/${batchId}` : `/sign/sign-with-email/${ref}`,
      onSuccess,
      onFailure,
    }),

  verifySigningWithCode: ({
    ref,
    batchId,
    verificationCode,
    onSuccess,
    onFailure,
  }: {
    ref: string;
    batchId?: string;
    verificationCode: string;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) =>
    httpPost({
      url: batchId
        ? `/sign/verify-batch-signing-with-code/${batchId}/${verificationCode}`
        : `/sign/verify-signing-with-code/${ref}/${verificationCode}`,
      onSuccess,
      onFailure,
    }),

  signWithHandwriting: ({
    ref,
    batchId,
    base64EncodedImage,
    onSuccess,
    onFailure,
  }: {
    ref: string;
    batchId?: string;
    base64EncodedImage: string;
    onSuccess: (res: HttpResponse<void>) => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }) => {
    const formData = new FormData();
    formData.append("base64EncodedImage", base64EncodedImage);
    return httpPost({
      url: batchId
        ? `/sign/sign-batch-handwriting/${batchId}`
        : `/sign/sign-with-handwriting/${ref}`,
      data: formData,
      onSuccess,
      onFailure,
    });
  },
} as const;

export const signWithHandwritingState = (
  ref: string,
  signingDetails: DocumentToSign,
  batchId?: string,
): SignWithHandwritingState => ({
  type: "sign-with-handwriting",
  ref,
  batchId,
  signingDetails,
  inputMode: "draw",
  typedText: signingDetails.signerName,
  font: "Sacramento",
});

export const signWithEmailState = (
  ref: string,
  signingDetails: DocumentToSign,
  batchId?: string,
): SignWithEmailState => ({
  type: "sign-with-email",
  ref,
  signingDetails,
  verificationCode: "",
  batchId,
});

export const initSignDocumentPageAction = (
  ref: string | undefined,
  batchId?: string,
  returnUrl?: string,
  mode?: PageMode,
): DCEvent[] => {
  if (!ref) {
    return [{ type: "update-store", data: { signDocument: { type: "invalid-ref" } } }];
  }
  return [
    {
      type: "update-store",
      data: { signDocument: { type: "loading-details", ref, batchId, returnUrl } },
    },
    signingApi.getSigningDetails({
      ref,
      onSuccess: (res) => [
        {
          type: "update-store",
          data: {
            signDocument: {
              type: res.data.documents[0].isSigned ? "signed" : "ready",
              ref,
              batchId,
              returnUrl,
              mode,
              signingDetails: res.data.documents[0],
            },
          },
        },
      ],
      onFailure: () => [{ type: "update-store", data: { signDocument: { type: "invalid-ref" } } }],
    }),
  ];
};

export const startSigningAction = (state: ReadyState): DCEvent[] => {
  switch (state.signingDetails.signingMethod) {
    case SigningMethod.BANKID_AUTH:
      return [
        {
          type: "update-session-storage",
          key: loginRedirectKey,
          val: `/signing-completed-bankid-callback${toQueryString({ "signing-complete-company": state.signingDetails.companyName })}`,
        },
        {
          type: "load-url",
          url: `/api/document/sign-with-bankid-auth/${state.ref}${toQueryString({
            returnUrl: state.returnUrl,
            autologon: true,
          })}`,
        },
      ];
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return [
        {
          type: "load-url",
          url: `/api/document/sign-with-bankid-sweden-auth/${state.ref}${toQueryString({ returnUrl: state.returnUrl })}`,
        },
      ];
    case SigningMethod.MITID_DENMARK_AUTH:
      return [
        {
          type: "load-url",
          url: `/api/document/sign-with-mitid-auth/${state.ref}${toQueryString({ returnUrl: state.returnUrl })}`,
        },
      ];
    case SigningMethod.HANDWRITING:
      return [
        {
          type: "update-store",
          data: { signDocument: signWithHandwritingState(state.ref, state.signingDetails) },
        },
      ];
    case SigningMethod.AUTH_EMAIL:
      return [
        { type: "update-store", data: { signDocument: { ...state, isStartingSigning: true } } },
        signingApi.signWithEmail({
          ref: state.ref,
          onSuccess: () => [
            {
              type: "update-store",
              data: { signDocument: signWithEmailState(state.ref, state.signingDetails) },
            },
          ],
          onFailure: () => [
            {
              type: "update-store",
              data: { signDocument: { ...state, isStartingSigning: false } },
            },
            { type: "show-alert", message: { key: "general-error" }, messageType: "error" },
          ],
        }),
      ];
    default:
      logError(`Unsupported signing method: '${state.signingDetails.signingMethod}'`);
      return [];
  }
};

export const startBatchSigningAction = (state: ReadyState): DCEvent[] => {
  switch (state.signingDetails.signingMethod) {
    case SigningMethod.BANKID_AUTH:
      return [
        {
          type: "load-url",
          url: `/api/sign/sign-batch-bankid?id=${state.batchId}&returnUrl=${state.returnUrl}`,
        },
      ];
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return [
        {
          type: "load-url",
          url: `/api/sign/sign-batch-bankid-sweden?id=${state.batchId}&returnUrl=${state.returnUrl}`,
        },
      ];
    case SigningMethod.HANDWRITING:
      return [
        {
          type: "update-store",
          data: {
            signDocument: signWithHandwritingState(state.ref, state.signingDetails, state.batchId),
          },
        },
      ];
    case SigningMethod.AUTH_EMAIL:
      return [
        { type: "update-store", data: { signDocument: { ...state, isStartingSigning: true } } },
        signingApi.signWithEmail({
          ref: state.ref,
          batchId: state.batchId,
          onSuccess: () => [
            {
              type: "update-store",
              data: {
                signDocument: signWithEmailState(state.ref, state.signingDetails, state.batchId),
              },
            },
          ],
          onFailure: () => [
            {
              type: "update-store",
              data: { signDocument: { ...state, isStartingSigning: false } },
            },
            { type: "show-alert", message: { key: "general-error" }, messageType: "error" },
          ],
        }),
      ];
    default:
      logError(`Unsupported signing method: '${state.signingDetails.signingMethod}'`);
      return [];
  }
};

const setVerificationCodeAction = (state: SignWithEmailState, value: string): DCEvent[] => [
  {
    type: "update-store",
    data: { signDocument: { ...state, verificationCode: value } },
  },
];

export const submitVerificationCodeAction = (state: SignWithEmailState): DCEvent[] =>
  when(state.verificationCode.replace(/[^0-9a-zA-Z]/g, ""), (verificationCode) => [
    signingApi.verifySigningWithCode({
      ref: state.ref,
      batchId: state.batchId,
      verificationCode,
      onSuccess: () => [
        {
          type: "update-store",
          data: {
            signDocument: { type: "signed", ref: state.ref, signingDetails: state.signingDetails },
          },
        },
      ],
      onFailure: () => [
        {
          type: "update-store",
          data: {
            signDocument: { ...state, signResult: failure() },
          },
        },
      ],
    }),
    { type: "update-store", data: { signDocument: { ...state, signResult: pending() } } },
  ]) ?? [];

export const retryAction = (): DCEvent[] => [reload()];

const setInputModeAction = (
  state: SignWithHandwritingState,
  inputMode: "draw" | "type",
): DCEvent[] => [{ type: "update-store", data: { signDocument: { ...state, inputMode } } }];

const setFontAction = (state: SignWithHandwritingState, font: SigningFont): DCEvent[] => [
  { type: "update-store", data: { signDocument: { ...state, font } } },
];

const setTypedTextAction = (
  state: SignWithHandwritingState,
  event: ChangeEvent<HTMLInputElement>,
): DCEvent[] => [
  { type: "update-store", data: { signDocument: { ...state, typedText: event.target.value } } },
];

const setReactSketchCanvasRefAction = (ref: ReactSketchCanvas | null): DCEvent[] => [
  {
    type: "update-store",
    data: {
      signDocument: (state) => {
        // Note need to do this with direct assignment (mobx style) to prevent infinite loop
        // when assigning the ref.
        if (state?.type === "sign-with-handwriting") {
          state.reactSketchCanvasRef = ref ?? undefined;
        }
        return state;
      },
    },
  },
];

export const exportSignature = (state: {
  inputMode: "draw" | "type";
  typedText: string;
  font: SigningFont;
  reactSketchCanvasRef?: ReactSketchCanvas;
}): Promise<string> => {
  if (state.inputMode === "draw" && state.reactSketchCanvasRef) {
    return state.reactSketchCanvasRef.exportImage("png").catch((err) => {
      logError(err, "Failed to export handwritten signature as base64 encoded PNG");
      return Promise.reject();
    });
  } else {
    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 150;
    const context = canvas.getContext("2d")!;
    context.fillStyle = "white";
    context.fillRect(0, 150, 800, 150);
    context.font = `60px '${state.font}', cursive`;
    context.fillStyle = "darkblue";
    context.fillText(state.typedText, 30, 90);
    return Promise.resolve(canvas.toDataURL());
  }
};

export const submitHandwrittenSignatureAction = (
  state: SignWithHandwritingState,
  base64EncodedImage: string,
): DCEvent[] => [
  signingApi.signWithHandwriting({
    ref: state.ref,
    batchId: state.batchId,
    base64EncodedImage,
    onSuccess: () => [
      {
        type: "update-store",
        data: {
          signDocument: { type: "signed", ref: state.ref, signingDetails: state.signingDetails },
        },
      },
    ],
    onFailure: () => [
      {
        type: "update-store",
        data: { signDocument: { ...state, signResult: failure() } },
      },
      { type: "show-alert", message: { key: "general-error" }, messageType: "error" },
    ],
  }),
  { type: "update-store", data: { signDocument: { ...state, signResult: pending() } } },
];

const downloadDocumentAction = (
  state: ReadyState | SignWithEmailState | SignWithHandwritingState | SignedState,
): DCEvent[] => [
  { type: "update-store", data: { signDocument: { ...state, isDownloadingDocument: true } } },
  ...downloadEvent({
    url: `/sign/download-document-for-signer/${state.ref}`,
    onSuccess: () => [
      {
        type: "update-store",
        data: { signDocument: { ...state, isDownloadingDocument: undefined } },
      },
    ],
    onFailure: () => [
      {
        type: "update-store",
        data: { signDocument: { ...state, isDownloadingDocument: undefined } },
      },
    ],
  }),
];

const failedToSignUIState = (
  state: SignWithHandwritingState | SignWithEmailState,
  dispatch: EventDispatcher,
  appBar: AppBarData | null,
  lang: Language,
  isEmailErrorHandlingSignRequest?: boolean,
): SignDocumentPageUIState => ({
  type: "done",
  header: prepareHeaderState(dispatch, state, appBar, lang),
  iconName: "warning-circle",
  iconColor: "warning",
  title: isEmailErrorHandlingSignRequest
    ? {
        key: "signing.errorHandlingSignRequest.incorrect-code",
      }
    : { key: "signing.errorHandlingSignRequest" },
  primaryAction: { label: { key: "signing.tryAgain" }, onClick: () => dispatch(retryAction()) },
  downloadDocument: () => dispatch(downloadDocumentAction(state)),
  isDownloadingDocument: state.isDownloadingDocument,
  alertType: "error",
});

const prepareHeaderState = (
  dispatch: EventDispatcher,
  state: SignDocumentPageState,
  appBar: AppBarData | null,
  lang: Language,
): HeaderUIState => ({
  title: state.signingDetails
    ? { translated: state.signingDetails.companyName }
    : { key: "signing.slogan" },
  links: [
    {
      id: "signing.externalLink1",
      label: { key: "signing.externalLink1" },
      onClick: () =>
        dispatch([{ type: "load-url", url: applicationFrontPageUrl(), newWindow: true }]),
    },
    {
      id: "signing.externalLink2",
      label: { key: "signing.externalLink2" },
      onClick: () => dispatch([{ type: "load-url", url: contactUsUrl, newWindow: true }]),
    },
  ],
  header: prepLandingPageHeader({
    appBar,
    lang,
    dispatch,
    user: when(state.signingDetails, ({ signerName, signerInitials, signerEmail }) => ({
      name: signerName,
      initials: signerInitials,
      email: signerEmail,
    })),
    title: when(state.signingDetails?.documentTitle, (documentTitle) => ({
      translated: documentTitle,
    })),
    companyContext: when(state.signingDetails?.companyName, (companyName) => ({
      title: { translated: companyName },
      subtitle: {
        key: "signing.subtitle",
        params: { n: state.signingDetails?.isSigned ? 1 : 0, count: 1 },
      },
    })),
  }),
});

export const prepareState = (
  dispatch: EventDispatcher,
  state: SignDocumentPageState = { type: "loading-details", ref: "" },
  appBar: AppBarData | null,
  lang: Language,
): SignDocumentPageUIState => {
  const header = prepareHeaderState(dispatch, state, appBar, lang);
  switch (state.type) {
    case "invalid-ref":
      return {
        type: "done",
        header,
        iconName: "warning-circle",
        iconColor: "warning",
        title: { key: "signing.invalidRefTitle" },
        description: { key: "signing.invalidRefText" },
        alertType: "error",
      };

    case "loading-details":
      return { type: "loading", header };

    case "ready":
      return {
        type: "ready",
        header,
        title: {
          key: "signing.helloTitleName",
          params: {
            signerName: state.signingDetails.signerName,
          },
        },
        description: {
          key: "signing.helloTitleIntro",
          params: {
            authorName: state.signingDetails.authorName,
            companyName: state.signingDetails.companyName,
          },
        },
        document: { type: "pdf", url: state.signingDetails.documentUrl },
        startSigning: {
          label: { key: state.batchId ? "signingDialog.sign-single-document" : "signing.sign" },
          onClick: () => dispatch(startSigningAction(state)),
          loading: state.isStartingSigning,
        },
        startBatchSigning:
          state.batchId && !(state.mode === "single")
            ? {
                label: { key: "signing.batchSign" },
                onClick: () => dispatch(startBatchSigningAction(state)),
                loading: state.isStartingSigning,
              }
            : undefined,
        downloadDocument: () => dispatch(downloadDocumentAction(state)),
        isDownloadingDocument: state.isDownloadingDocument,
        authorName: state.signingDetails.authorName,
        companyName: state.signingDetails.companyName,
      };

    case "signed":
      return {
        type: "done",
        header,
        iconName: "check-circle",
        iconColor: "success",
        title: { key: "signing.documentSigned" },
        description: { key: "signing.documentSigned.description" },
        document: { type: "pdf", url: state.signingDetails.documentUrl },
        downloadDocument: () => dispatch(downloadDocumentAction(state)),
        isDownloadingDocument: state.isDownloadingDocument,
        alertType: "success",
      };

    case "sign-with-email":
      if (isFailure(state.signResult)) {
        return failedToSignUIState(state, dispatch, appBar, lang, true);
      }
      return {
        type: "sign-with-email",
        header,
        message: {
          key: "signing.sendCode.AUTH_EMAIL",
          params: {
            signerName: state.signingDetails.signerName,
          },
        },
        verificationCode: {
          label: { key: "signing.inputCode" },
          value: state.verificationCode,
          onChange: (event) => dispatch(setVerificationCodeAction(state, event.target.value)),
        },
        submitVerificationCode: {
          label: { key: "signing.verifySigningWithCode" },
          onClick: () => dispatch(submitVerificationCodeAction(state)),
          loading: isPending(state.signResult),
        },
      };

    case "sign-with-handwriting":
      if (isFailure(state.signResult)) {
        return failedToSignUIState(state, dispatch, appBar, lang);
      }
      return {
        type: "sign-with-handwriting",
        header,
        message: {
          key: "signing.sendCode.HANDWRITING",
          params: {
            signerName: state.signingDetails.signerName,
          },
        },
        inputMode: {
          selected: state.inputMode,
          options: ["draw", "type"],
          idFn: identity,
          labelFn: (value) =>
            value === "draw" ? { key: "signing.handWritten" } : { key: "signing.byTyping" },
          onChange: (_, value) => dispatch(setInputModeAction(state, value)),
        },
        font: {
          value: state.font,
          items: signingFonts,
          onSelect: (value) => dispatch(setFontAction(state, value)),
          idFn: identity,
          labelFn: (value) => ({ translated: value }),
        },
        typedText: {
          value: state.typedText,
          onChange: (event) => dispatch(setTypedTextAction(state, event)),
        },
        submit: {
          label: isPending(state.signResult)
            ? { key: "signing.signingInProgress" }
            : { key: "signing.sign" },
          loading: isPending(state.signResult),
          onClick: () =>
            exportSignature(state).then((signature) =>
              dispatch(submitHandwrittenSignatureAction(state, signature)),
            ),
        },
        setReactSketchCanvasRef: (ref: ReactSketchCanvas | null) =>
          dispatch(setReactSketchCanvasRefAction(ref)),
      };
  }
};
